import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authedFetch } from '../api'

export interface ReportListItem {
  id: string
  title: string
  tag_filter: string
  search_filter: string
  status: string
  created_at: string
}

export interface ReportDetail {
  id: string
  title: string
  tag_filter: string
  search_filter: string
  status: string
  error: string
  summary_data: string
  created_at: string
}

interface ReportsState {
  items: ReportListItem[]
  loading: boolean
  error: string | null
  currentReport: ReportDetail | null
  currentReportLoading: boolean
  currentReportError: string | null
}

const initialState: ReportsState = {
  items: [],
  loading: false,
  error: null,
  currentReport: null,
  currentReportLoading: false,
  currentReportError: null,
}

export const fetchReports = createAsyncThunk('reports/fetchReports', async () => {
  const response = await fetch('/api/reports')
  if (!response.ok) throw new Error('Failed to fetch reports')
  return response.json() as Promise<{ reports: ReportListItem[] }>
})

export const fetchReportDetail = createAsyncThunk('reports/fetchReportDetail', async (id: string) => {
  const response = await fetch(`/api/reports/${id}`)
  if (!response.ok) throw new Error('Failed to fetch report')
  return response.json() as Promise<ReportDetail>
})

export const pollReportStatus = createAsyncThunk('reports/pollReportStatus', async (id: string) => {
  const response = await fetch(`/api/reports/${id}/status`)
  if (!response.ok) throw new Error('Failed to poll report status')
  return response.json() as Promise<{ status: string; error: string }>
})

export const deleteReport = createAsyncThunk('reports/deleteReport', async (id: string) => {
  const response = await authedFetch(`/api/reports/${id}`, { method: 'DELETE' })
  if (!response.ok) throw new Error('Failed to delete report')
  return id
})

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state) => { state.loading = true; state.error = null })
      .addCase(fetchReports.fulfilled, (state, action) => { state.loading = false; state.items = action.payload.reports })
      .addCase(fetchReports.rejected, (state, action) => { state.loading = false; state.error = action.error.message ?? 'Failed to fetch reports' })
      .addCase(fetchReportDetail.pending, (state) => { state.currentReportLoading = true; state.currentReportError = null })
      .addCase(fetchReportDetail.fulfilled, (state, action) => { state.currentReportLoading = false; state.currentReport = action.payload })
      .addCase(fetchReportDetail.rejected, (state, action) => { state.currentReportLoading = false; state.currentReportError = action.error.message ?? 'Failed to fetch report' })
      .addCase(pollReportStatus.fulfilled, (state, action) => {
        if (state.currentReport) {
          state.currentReport.status = action.payload.status
          state.currentReport.error = action.payload.error
        }
      })
      .addCase(deleteReport.fulfilled, (state, action) => { state.items = state.items.filter(r => r.id !== action.payload) })
  },
})

export default reportsSlice.reducer
